import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Loader2,
  Search,
  Eye,
  Calendar,
  FileText,
  X,
  Download,
  CheckSquare,
  Square,
  Star,
} from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import Header from "../components/Header";
import LoadingLogo from "../components/LoadingLogo";
import { getAuthenticatedAxios } from "../utils/api";
import highlightService from "../services/highlightService";
import { enqueueResultEngagementEvent } from "../services/resultEngagementService";
import { getCriteriaSelectionText } from "../features/search/utils/historyCriteria";
import {
  getHistoryCreditsLabel,
  getHistorySearchModeLabel,
  getHistorySearchTypeLabel,
  shouldShowHistorySearchType,
} from "../features/search/utils/historySearchMetadata";
import { normalizeHistoryDetails } from "../features/search/utils/historyPaperNormalization";

function SearchHistory() {
  const getLocalYYYYMMDD = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSearches, setSelectedSearches] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [showHighlightedOnly, setShowHighlightedOnly] = useState(false);
  const [allHighlights, setAllHighlights] = useState([]);
  const [searchHighlights, setSearchHighlights] = useState({});
  const [startDate, setStartDate] = useState(() => getLocalYYYYMMDD());
  const [endDate, setEndDate] = useState(() => getLocalYYYYMMDD());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalSearches, setTotalSearches] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const formatCriteriaSummary = useCallback(
    (search) =>
      getCriteriaSelectionText({
        criteriaSet: search?.criteria_set,
        language: i18n.language,
      }),
    [i18n.language],
  );

  const formatSearchTypeSummary = useCallback(
    (search) =>
      shouldShowHistorySearchType(search)
        ? getHistorySearchTypeLabel({ search, t })
        : "",
    [t],
  );

  // Get current language
  const language = i18n.language;

  const loadSearchHistory = useCallback(
    async (page = currentPage, itemsPerPage = perPage) => {
      setLoading(true);
      try {
        const authAxios = getAuthenticatedAxios();

        // Build query parameters for pagination
        const params = new URLSearchParams({
          page: page.toString(),
          per_page: itemsPerPage.toString(),
        });

        let normalizedStart = startDate;
        let normalizedEnd = endDate;
        if (normalizedStart && normalizedEnd && normalizedStart > normalizedEnd) {
          const swap = normalizedStart;
          normalizedStart = normalizedEnd;
          normalizedEnd = swap;
        }

        if (normalizedStart) params.append("start_date", normalizedStart);
        if (normalizedEnd) params.append("end_date", normalizedEnd);

        const response = await authAxios.get(
          `/api/v1/search-history?${params}`,
        );

        // Backend returns paginated response with { searches: [], total: 0, page: 1, per_page: 20, has_next: false }
        const searchesData = response.data.searches || [];
        const total = response.data.total || 0;
        const currentPageNum = response.data.page || page;
        const perPageNum = response.data.per_page || itemsPerPage;

        setSearches(searchesData);
        setTotalSearches(total);
        setCurrentPage(currentPageNum);
        setPerPage(perPageNum);
        setTotalPages(Math.ceil(total / perPageNum));
      } catch (error) {
        alert(`${t("common.error")}: ${error.message}`);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, perPage, t, startDate, endDate],
  );

  const loadAllHighlights = useCallback(async () => {
    try {
      const highlights = await highlightService.getAllHighlights();
      setAllHighlights(highlights);

      // Build a map of searchId to highlighted PMIDs
      const highlightMap = {};
      highlights.forEach((highlight) => {
        if (!highlightMap[highlight.search_id]) {
          highlightMap[highlight.search_id] = new Set();
        }
        highlightMap[highlight.search_id].add(highlight.pmid);
      });
      setSearchHighlights(highlightMap);
    } catch (error) {
      // Failed to load highlights - silently continue
    }
  }, []);

  useEffect(() => {
    loadSearchHistory();
    loadAllHighlights();
  }, [loadSearchHistory, loadAllHighlights]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === "ja" ? "ja-JP" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Handle language change
  const setLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("pubmed-search-language", lang);
  };

  // Filter searches based on search term and highlights
  // NOTE: This is client-side filtering on already paginated data from the backend.
  // This means filtering only applies to the current page of results, not all searches.
  // For full filtering across all data, server-side filtering would need to be implemented.
  const filteredSearches = searches.filter((search) => {
    // First apply highlight filter if enabled
    if (showHighlightedOnly) {
      // Only show searches that have highlighted papers
      if (
        !searchHighlights[search.id] ||
        searchHighlights[search.id].size === 0
      ) {
        return false;
      }
    }

    // Then apply text search filter
    if (searchTerm === "") return true;

    const query = search.query || "";
    const searchTypeText = formatSearchTypeSummary(search);
    const searchModeText = getHistorySearchModeLabel({ searchMode: search.search_mode, t });
    const criteriaText = formatCriteriaSummary(search);
    const searchId = search.id || "";
    const resultCount = (search.result_count || 0).toString();

    // Fuzzy search across multiple fields
    return (
      query.toLowerCase().includes(searchTerm.toLowerCase()) ||
      searchModeText.toLowerCase().includes(searchTerm.toLowerCase()) ||
      searchTypeText.toLowerCase().includes(searchTerm.toLowerCase()) ||
      criteriaText.toLowerCase().includes(searchTerm.toLowerCase()) ||
      searchId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resultCount.includes(searchTerm)
    );
  });

  // Toggle individual search selection
  const toggleSearchSelection = (searchId) => {
    const newSelected = new Set(selectedSearches);
    if (newSelected.has(searchId)) {
      newSelected.delete(searchId);
    } else {
      newSelected.add(searchId);
    }
    setSelectedSearches(newSelected);
  };

  // Toggle all searches selection
  const toggleAllSearches = () => {
    if (selectedSearches.size === filteredSearches.length) {
      setSelectedSearches(new Set());
    } else {
      setSelectedSearches(new Set(filteredSearches.map((s) => s.id)));
    }
  };

  // Generate CSV content for a single search
  const generateCSV = (search, papers) => {
    const escapeCsvCell = (value) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;

    const extractOverviewText = (overview) => {
      if (typeof overview === "string") {
        return overview.trim();
      }
      if (!overview || typeof overview !== "object") {
        return "";
      }
      const blocks = Array.isArray(overview.text_blocks) ? overview.text_blocks : [];
      const lines = [];

      blocks.forEach((block) => {
        if (!block || typeof block !== "object") {
          return;
        }
        const type = String(block.type || "");
        if (type === "heading") {
          const heading = String(block.snippet || "").trim();
          if (heading) {
            lines.push(`## ${heading}`);
          }
          return;
        }
        if (type === "list") {
          const listItems = Array.isArray(block.list) ? block.list : [];
          listItems.forEach((item) => {
            const itemText = typeof item === "object" ? item?.snippet : item;
            const normalized = String(itemText || "").trim();
            if (normalized) {
              lines.push(`- ${normalized}`);
            }
          });
          return;
        }
        const snippet = String(block.snippet || "").trim();
        if (snippet) {
          lines.push(snippet);
        }
      });

      return lines.join("\n").trim();
    };

    const extractOverviewReferences = (overview) => {
      if (!overview || typeof overview !== "object") {
        return "";
      }
      const refs = Array.isArray(overview.references) ? overview.references : [];
      const formatted = refs
        .map((ref) => {
          if (!ref || typeof ref !== "object") {
            return "";
          }
          const idx = Number(ref.index);
          const displayIndex = Number.isFinite(idx) ? idx + 1 : ref.index;
          const title = String(ref.title || "").trim();
          const link = String(ref.link || "").trim();
          if (!title && !link) {
            return "";
          }
          const titlePart = title || link;
          if (link) {
            return `[${displayIndex}] ${titlePart} (${link})`;
          }
          return `[${displayIndex}] ${titlePart}`;
        })
        .filter(Boolean);
      return formatted.join(" | ");
    };

    const buildOverviewCsvPrefixLines = (overview) => {
      const overviewText = extractOverviewText(overview);
      const overviewReferences = extractOverviewReferences(overview);
      if (!overviewText && !overviewReferences) {
        return [];
      }
      const lines = [];
      if (overviewText) {
        lines.push(`${escapeCsvCell("AI_OVERVIEW_TEXT")},${escapeCsvCell(overviewText)}`);
      }
      if (overviewReferences) {
        lines.push(
          `${escapeCsvCell("AI_OVERVIEW_REFERENCES")},${escapeCsvCell(overviewReferences)}`,
        );
      }
      lines.push("");
      return lines;
    };

    const headers = [
      t("csvExport.title"),
      t("csvExport.authors"),
      t("csvExport.journal"),
      t("csvExport.year"),
      t("csvExport.pmid"),
      t("csvExport.url"),
      t("csvExport.relevanceScore"),
      t("csvExport.citations"),
      t("csvExport.researchQuestion"),
      t("csvExport.design"),
      t("csvExport.population"),
      t("csvExport.exposureIntervention"),
      t("csvExport.endpoint"),
      t("csvExport.statistics"),
      t("csvExport.keyResults"),
      t("csvExport.selectionRationale"),
      t("csvExport.searchQuery"),
      t("csvExport.searchDate"),
      t("csvExport.preferenceType"),
    ];

    const rows = papers.map((paper) => [
      paper.title || "",
      (paper.authors || []).join("; "),
      paper.journal || "",
      paper.pub_year || paper.year || "",
      paper.pmid || "",
      paper.url || "",
      paper.relevance_score
        ? (paper.relevance_score * 100).toFixed(1) + "%"
        : "",
      paper.cited_by || "",
      paper.research_question || "",
      paper.design || "",
      paper.population || "",
      paper.exposure_intervention || "",
      paper.endpoint || "",
      paper.statistics || "",
      paper.key_results || "",
      paper.rationale || "",
      search.query || "",
      formatDate(search.created_at),
      formatCriteriaSummary(search) || search.search_type || "",
      ]);

    // Escape and format CSV
    const overviewPrefixLines = buildOverviewCsvPrefixLines(search?.ai_overview);
    const csvContent = [
      ...overviewPrefixLines,
      headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      ),
    ].join("\n");

    // Add BOM for UTF-8 to help Excel recognize the encoding
    return "\uFEFF" + csvContent;
  };

  // Convert CSV to appropriate encoding
  const prepareCSVBlob = (csvContent) => {
    // UTF-8 with BOM for all platforms
    return new Blob([csvContent], {
      type: "text/csv;charset=utf-8",
    });
  };

  // Export selected searches
  const handleExport = async (highlightedOnly = false) => {
    if (selectedSearches.size === 0) {
      alert(t("csvExport.noSelection"));
      return;
    }

    setExporting(true);
    try {
      const authAxios = getAuthenticatedAxios();
      const searchesToExport = [];

      // Fetch details for each selected search
      for (const searchId of selectedSearches) {
        const response = await authAxios.get(
          `/api/v1/search-history/${searchId}/details`,
        );
        const normalizedDetails = normalizeHistoryDetails(response.data);

        // Filter papers if exporting highlighted only
        if (highlightedOnly && searchHighlights[searchId]) {
          const highlightedPapers =
            normalizedDetails.papers?.filter((paper) => {
              const pmid = paper.pmid || paper.PMID;
              return searchHighlights[searchId].has(pmid);
            }) || [];

          // Only add if there are highlighted papers
          if (highlightedPapers.length > 0) {
            searchesToExport.push({
              ...normalizedDetails,
              papers: highlightedPapers,
            });
          }
        } else {
          searchesToExport.push(normalizedDetails);
        }
      }

      if (highlightedOnly && searchesToExport.length === 0) {
        alert(t("searchHistory.noHighlightedPapersToExport"));
        setExporting(false);
        return;
      }

      searchesToExport.forEach((search) => {
        enqueueResultEngagementEvent({
          event_type: "csv_export",
          search_id: search.id || search.search_id,
          query: search.query || undefined,
          search_mode: search.search_mode || search.search_type || undefined,
          surface: "search_history",
          export_scope: selectedSearches.size > 1 ? "multiple_searches" : "single_search",
          export_count: search.papers?.length || 0,
          highlighted_only: highlightedOnly,
          metadata: {
            selected_search_count: selectedSearches.size,
          },
        });
      });

      if (searchesToExport.length === 1) {
        // Single CSV file
        const search = searchesToExport[0];
        const csvContent = generateCSV(search, search.papers || []);
        const blob = prepareCSVBlob(csvContent);
        // Clean filename but keep Japanese characters
        const queryPart = search.query
          .substring(0, 30)
          .replace(/[<>:"/\\|?*]/g, "_");
        const prefix = highlightedOnly ? "highlighted_" : "";
        const fileName = `${prefix}${queryPart}_${new Date(search.created_at).toISOString().split("T")[0]}.csv`;
        saveAs(blob, fileName);
      } else {
        // Multiple CSV files in a zip
        const zip = new JSZip();

        // Process each search and add to zip
        for (let i = 0; i < searchesToExport.length; i++) {
          const search = searchesToExport[i];
          const csvContent = generateCSV(search, search.papers || []);
          const blob = prepareCSVBlob(csvContent);

          // Convert blob to array buffer for zip
          const arrayBuffer = await blob.arrayBuffer();

          // Clean filename but keep Japanese characters
          const queryPart = search.query
            .substring(0, 30)
            .replace(/[<>:"/\\|?*]/g, "_");
          // Add index to filename to ensure uniqueness
          const prefix = highlightedOnly ? "highlighted_" : "";
          const fileName = `${i + 1}_${prefix}${queryPart}_${new Date(search.created_at).toISOString().split("T")[0]}.csv`;
          zip.file(fileName, arrayBuffer);
        }

        const content = await zip.generateAsync({ type: "blob" });
        const zipPrefix = highlightedOnly ? "highlighted_" : "";
        saveAs(
          content,
          `${zipPrefix}pubmed_searches_${new Date().toISOString().split("T")[0]}.zip`,
        );
      }

      alert(t("csvExport.completed"));
      setSelectedSearches(new Set());
    } catch (error) {
      alert(t("csvExport.failed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header language={language} setLanguage={setLanguage} />

      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {t("searchHistory.title")}
            </h2>
            <button
              onClick={() => navigate("/")}
              className="px-4 py-2.5 text-sm sm:text-base font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2"
            >
              <Search className="h-4 w-4" />
              {t("searchHistory.backToSearch")}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto p-6">
        {/* Search Filter and Export Controls */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t(
                  "searchHistory.filterPlaceholder",
                  "検索キーワード等をご入力下さい",
                )}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  // Reset to first page when search term changes
                  if (currentPage !== 1) {
                    setCurrentPage(1);
                    loadSearchHistory(1, perPage);
                  }
                }}
                className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    // Reset to first page when clearing search
                    if (currentPage !== 1) {
                      setCurrentPage(1);
                      loadSearchHistory(1, perPage);
                    }
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {filteredSearches.length > 0 && (
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => {
                    setShowHighlightedOnly(!showHighlightedOnly);
                    // Reset to first page when changing filter
                    if (currentPage !== 1) {
                      setCurrentPage(1);
                      loadSearchHistory(1, perPage);
                    }
                  }}
                  className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                    showHighlightedOnly
                      ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Star
                    className={`h-4 w-4 ${showHighlightedOnly ? "fill-current" : ""}`}
                  />
                  <span>{t("searchHistory.showHighlighted")}</span>
                  {allHighlights.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-white/50 rounded">
                      {allHighlights.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={toggleAllSearches}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                >
                  {selectedSearches.size === filteredSearches.length ? (
                    <CheckSquare className="h-4 w-4" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                  {selectedSearches.size === filteredSearches.length
                    ? t("searchHistory.deselectAll")
                    : t("searchHistory.selectAll")}
                </button>
                {selectedSearches.size > 0 && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleExport(false)}
                      disabled={exporting}
                      className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {exporting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                      {exporting
                        ? t("csvExport.preparing")
                        : t("searchHistory.exportSelected", {
                            count: selectedSearches.size,
                          })}
                    </button>
                    {allHighlights.length > 0 && (
                      <button
                        onClick={() => handleExport(true)}
                        disabled={exporting}
                        className="px-3 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <Star className="h-4 w-4 fill-current" />
                        {t("searchHistory.exportHighlighted")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1">
                {t("searchHistory.dateFrom")}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (currentPage !== 1) {
                    setCurrentPage(1);
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1">
                {t("searchHistory.dateTo")}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  if (currentPage !== 1) {
                    setCurrentPage(1);
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {(startDate || endDate) && (
              <button
                onClick={() => {
                  const today = getLocalYYYYMMDD();
                  setStartDate(today);
                  setEndDate(today);
                  if (currentPage !== 1) {
                    setCurrentPage(1);
                  }
                }}
                className="h-9 px-3 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t("common.today")}
              </button>
            )}
          </div>
          <div className="mt-2 text-xs text-gray-500 flex justify-between items-center">
            {searchTerm && (
              <span>
                {t("searchHistory.showingFiltered", {
                  filtered: filteredSearches.length,
                  total: searches.length,
                })}
              </span>
            )}
            {!searchTerm && (
              <span>
                {t("searchHistory.showingResults", {
                  from: (currentPage - 1) * perPage + 1,
                  to: Math.min(currentPage * perPage, totalSearches),
                  total: totalSearches,
                })}
              </span>
            )}
            <div className="flex items-center gap-2">
              <span>{t("searchHistory.perPage")}:</span>
              <select
                value={perPage}
                onChange={(e) => {
                  const newPerPage = Number(e.target.value);
                  setPerPage(newPerPage);
                  loadSearchHistory(1, newPerPage);
                }}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingLogo
                size="md"
                message={t("searchHistory.loadingHistory")}
              />
            </div>
          ) : searches.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{t("searchHistory.noSearches")}</p>
            </div>
          ) : filteredSearches.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {t("searchHistory.noMatchingSearches")}
              </p>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setShowHighlightedOnly(false);
                  // Reset to first page when clearing all filters
                  if (currentPage !== 1) {
                    setCurrentPage(1);
                    loadSearchHistory(1, perPage);
                  }
                }}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 underline"
              >
                {t("searchHistory.clearFilter")}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={toggleAllSearches}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        {selectedSearches.size === filteredSearches.length ? (
                          <CheckSquare className="h-4 w-4" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("searchHistory.date")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("searchHistory.searchQuery")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("searchHistory.mode")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("searchHistory.selectedCriteria")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("searchHistory.results")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("searchHistory.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSearches.map((search) => {
                    const modeLabel = getHistorySearchModeLabel({
                      searchMode: search.search_mode,
                      t,
                    });
                    const creditsLabel = getHistoryCreditsLabel({
                      chargedCredits: search.charged_credits,
                      t,
                    });

                    return (
                    <tr key={search.id} className="hover:bg-gray-50">
                      <td className="px-3 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleSearchSelection(search.id)}
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                        >
                          {selectedSearches.has(search.id) ? (
                            <CheckSquare className="h-4 w-4 text-blue-600" />
                          ) : (
                            <Square className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          {formatDate(search.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="flex items-start gap-2">
                          <div className="max-w-md break-words">
                            {search.query}
                          </div>
                          {searchHighlights[search.id] &&
                            searchHighlights[search.id].size > 0 && (
                              <span className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full flex-shrink-0">
                                <Star className="h-3 w-3 fill-current" />
                                {searchHighlights[search.id].size}
                              </span>
                            )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-col items-start gap-2">
                          {modeLabel ? (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                              {modeLabel}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                          <span className="text-xs text-gray-500">
                            {creditsLabel || "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatCriteriaSummary(search) ? (
                          <div className="max-w-md break-words leading-relaxed">
                            {formatCriteriaSummary(search)}
                          </div>
                        ) : formatSearchTypeSummary(search) ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {formatSearchTypeSummary(search)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <FileText className="h-4 w-4 mr-2" />
                          {search.result_count || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <a
                          href={`/search-history/${search.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-900 mr-4 inline-flex items-center"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {t("searchHistory.viewDetails")}
                        </a>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {!loading && totalSearches > 0 && (
          <div className="bg-white rounded-lg shadow mt-6">
            <div className="px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                {/* Results info */}
                <div className="text-sm text-gray-700">
                  {searchTerm || showHighlightedOnly ? (
                    // When filtering, show count of filtered results on current page
                    <>
                      {t("common.showing")}{" "}
                      <span className="font-medium">
                        {filteredSearches.length}
                      </span>{" "}
                      {t("searchHistory.searches")}{" "}
                      <span className="text-yellow-600">
                        ({t("common.filteredFromPage")})
                      </span>
                    </>
                  ) : (
                    // When not filtering, show normal pagination info
                    <>
                      {t("common.showing")}{" "}
                      <span className="font-medium">
                        {Math.min(
                          (currentPage - 1) * perPage + 1,
                          totalSearches,
                        )}
                      </span>{" "}
                      {t("common.to")}{" "}
                      <span className="font-medium">
                        {Math.min(currentPage * perPage, totalSearches)}
                      </span>{" "}
                      {t("common.of")}{" "}
                      <span className="font-medium">{totalSearches}</span>{" "}
                      {t("searchHistory.searches")}
                    </>
                  )}
                </div>

                {/* Page controls */}
                <div className="flex items-center space-x-2">
                  {/* Per page selector */}
                  <select
                    value={perPage}
                    onChange={(e) => {
                      const newPerPage = Number(e.target.value);
                      setPerPage(newPerPage);
                      setCurrentPage(1); // Reset to first page when changing items per page
                      loadSearchHistory(1, newPerPage);
                    }}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                  >
                    <option value={10}>10 {t("common.perPage")}</option>
                    <option value={20}>20 {t("common.perPage")}</option>
                    <option value={50}>50 {t("common.perPage")}</option>
                    <option value={100}>100 {t("common.perPage")}</option>
                  </select>

                  {/* Previous button */}
                  <button
                    onClick={() => loadSearchHistory(currentPage - 1, perPage)}
                    disabled={currentPage <= 1}
                    className={`px-3 py-1 border rounded-md text-sm ${
                      currentPage <= 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {t("common.previous")}
                  </button>

                  {/* Page numbers */}
                  <div className="flex items-center space-x-1">
                    {/* Show page numbers intelligently */}
                    {totalPages <= 7 ? (
                      // Show all pages if 7 or less
                      Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (pageNum) => (
                          <button
                            key={pageNum}
                            onClick={() => loadSearchHistory(pageNum, perPage)}
                            className={`px-3 py-1 border rounded-md text-sm ${
                              pageNum === currentPage
                                ? "bg-blue-600 text-white"
                                : "bg-white text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {pageNum}
                          </button>
                        ),
                      )
                    ) : (
                      // Show smart pagination for many pages
                      <>
                        {currentPage > 2 && (
                          <>
                            <button
                              onClick={() => loadSearchHistory(1, perPage)}
                              className="px-3 py-1 border rounded-md text-sm bg-white text-gray-700 hover:bg-gray-50"
                            >
                              1
                            </button>
                            {currentPage > 3 && (
                              <span className="px-2">...</span>
                            )}
                          </>
                        )}

                        {/* Current page and neighbors */}
                        {[currentPage - 1, currentPage, currentPage + 1]
                          .filter(
                            (pageNum) => pageNum > 0 && pageNum <= totalPages,
                          )
                          .map((pageNum) => (
                            <button
                              key={pageNum}
                              onClick={() =>
                                loadSearchHistory(pageNum, perPage)
                              }
                              className={`px-3 py-1 border rounded-md text-sm ${
                                pageNum === currentPage
                                  ? "bg-blue-600 text-white"
                                  : "bg-white text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {pageNum}
                            </button>
                          ))}

                        {currentPage < totalPages - 1 && (
                          <>
                            {currentPage < totalPages - 2 && (
                              <span className="px-2">...</span>
                            )}
                            <button
                              onClick={() =>
                                loadSearchHistory(totalPages, perPage)
                              }
                              className="px-3 py-1 border rounded-md text-sm bg-white text-gray-700 hover:bg-gray-50"
                            >
                              {totalPages}
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>

                  {/* Next button */}
                  <button
                    onClick={() => loadSearchHistory(currentPage + 1, perPage)}
                    disabled={currentPage >= totalPages}
                    className={`px-3 py-1 border rounded-md text-sm ${
                      currentPage >= totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {t("common.next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default SearchHistory;
